/** Who the client is. The only mandatory step — everything after it is skippable.
 *
 *  Ported from the NewClient dialog this wizard replaced, with two changes:
 *
 *  - **No company picker.** `CreateTraineeRequest` has no `companyId`, `AdminTraineeController`
 *    passes null and the service derives the company from the principal — and it *rejects* a null
 *    company for SYSTEM_ADMIN, who also cannot route to `/app/clients` at all. The old selector was
 *    unreachable and inert.
 *  - **Password folded in** rather than a second step. The page has the room the 700px dialog
 *    did not, and splitting four fields across two screens bought nothing. */

import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { DateField } from "@mui/x-date-pickers";
import {
  GroupAdd as GroupAddIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import { useState } from "react";
import { t } from "../../../dashboard/coach/coachTokens";
import { avatarPalette } from "../../../ui/colors";
import PhoneInput from "../../../shared/PhoneInput";
import StepShell, { StepGrid } from "../StepShell";
import WizardField from "../WizardField";
import { choiceCardSx, fieldSx, numberFieldSx, selectSx } from "../wizardStyles";
import type { ExistingUser, IdentityValues } from "../wizardReducer";

const avatarFor = (name?: string) =>
  avatarPalette[name ? name.charCodeAt(0) % avatarPalette.length : 0];

interface IdentityStepProps {
  values: IdentityValues;
  error: string | null;
  /** Set when the server rejected this address, or the pre-check found it taken. */
  emailError: string | null;
  trainers: Array<{ id: number; name?: string }>;
  existingUsers: ExistingUser[];
  usersLoading: boolean;
  /** A company admin or manager must name a trainer; a trainer is assigned to themselves. */
  requiresTrainer: boolean;
  onChange: (values: Partial<IdentityValues>) => void;
}

const IdentityStep = ({
  values,
  error,
  emailError,
  trainers,
  existingUsers,
  usersLoading,
  requiresTrainer,
  onChange,
}: IdentityStepProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const passwordsMismatch =
    values.passwordConfirm !== "" && values.password !== values.passwordConfirm;

  const trainerField = requiresTrainer && (
    <WizardField label="מאמן מוצמד" required>
      <FormControl fullWidth size="small">
        <Select
          value={values.trainerId}
          displayEmpty
          inputProps={{ "aria-label": "מאמן מוצמד" }}
          sx={selectSx}
          onChange={event => onChange({ trainerId: String(event.target.value) })}
        >
          <MenuItem value="">לא נבחר</MenuItem>
          {trainers.map(trainer => (
            <MenuItem key={trainer.id} value={String(trainer.id)}>
              {trainer.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </WizardField>
  );

  if (values.source === "choose") {
    return (
      <StepShell title="איך להוסיף את הלקוח?" error={error}>
        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "14px" }}>
          <Box
            component="button"
            type="button"
            onClick={() => onChange({ source: "new" })}
            sx={choiceCardSx(false)}
          >
            <GroupAddIcon sx={{ fontSize: 26, color: t.green }} />
            <Box sx={{ fontSize: "14px", fontWeight: 700 }}>לקוח חדש</Box>
            <Box sx={{ fontSize: "12px", color: t.t2, lineHeight: 1.45 }}>
              יצירת חשבון חדש מאפס, כולל סיסמה ומייל הצטרפות
            </Box>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={() => onChange({ source: "existing" })}
            sx={choiceCardSx(false)}
          >
            <PersonIcon sx={{ fontSize: 26, color: t.green }} />
            <Box sx={{ fontSize: "14px", fontWeight: 700 }}>משתמש קיים</Box>
            <Box sx={{ fontSize: "12px", color: t.t2, lineHeight: 1.45 }}>
              הפיכת משתמש שכבר רשום במערכת למתאמן — ללא סיסמה חדשה
            </Box>
          </Box>
        </Box>
      </StepShell>
    );
  }

  if (values.source === "existing") {
    const selected = values.existingUser;

    return (
      <StepShell
        title="בחירת משתמש קיים"
        description="המשתמש כבר רשום במערכת, ולכן אין צורך בסיסמה חדשה."
        error={error}
      >
        <Box sx={{ maxWidth: "520px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <WizardField label="משתמש" required>
            <Autocomplete
              value={selected}
              loading={usersLoading}
              loadingText="טוען משתמשים..."
              noOptionsText="לא נמצאו משתמשים"
              options={existingUsers}
              getOptionLabel={option => option?.name || ""}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              onChange={(_, user) =>
                onChange({
                  existingUser: user,
                  name: user?.name || "",
                  email: user?.email || "",
                  gender: user?.gender || "",
                  phoneNumber: user?.phoneNumber || "",
                })
              }
              sx={fieldSx}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  sx={{ display: "flex", alignItems: "center", gap: "10px", padding: "8px 12px !important" }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "12px",
                      bgcolor: avatarFor(option.name),
                      color: t.white,
                    }}
                  >
                    {option.name?.charAt(0)}
                  </Avatar>
                  <Box>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 500, color: t.text }}>
                      {option.name}
                    </Box>
                    {option.email && (
                      <Box sx={{ fontSize: "11.5px", color: t.t3 }} dir="ltr">
                        {option.email}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  size="small"
                  placeholder="חפש משתמש..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <SearchIcon sx={{ color: t.t3, fontSize: 19, marginInlineEnd: "4px" }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </WizardField>

          {trainerField}
        </Box>
      </StepShell>
    );
  }

  return (
    <StepShell title="פרטי הלקוח" error={error}>
      <StepGrid>
        <WizardField label="שם מלא" required>
          <TextField
            fullWidth
            size="small"
            value={values.name}
            inputProps={{ "aria-label": "שם מלא" }}
            sx={fieldSx}
            onChange={event => onChange({ name: event.target.value })}
          />
        </WizardField>

        <WizardField label="אימייל" required error={emailError}>
          <TextField
            fullWidth
            size="small"
            value={values.email}
            error={Boolean(emailError)}
            inputProps={{ dir: "ltr", "aria-label": "אימייל" }}
            sx={fieldSx}
            onChange={event => onChange({ email: event.target.value })}
          />
        </WizardField>

        <WizardField label="טלפון" required>
          {/* `error`/`helperText` are destructured without defaults in PhoneInput.js, so they are
              required props. Passing false/"" hands the field back to its own validation, which
              is what renders "מספר לא תקין" as you type. */}
          <PhoneInput
            value={values.phoneNumber}
            onChange={(value: string) => onChange({ phoneNumber: value })}
            error={false}
            helperText=""
          />
        </WizardField>

        <WizardField label="מגדר" required>
          <FormControl fullWidth size="small">
            <Select
              value={values.gender}
              displayEmpty
              inputProps={{ "aria-label": "מגדר" }}
              sx={selectSx}
              onChange={event =>
                onChange({ gender: event.target.value as IdentityValues["gender"] })
              }
            >
              <MenuItem value="">לא נבחר</MenuItem>
              <MenuItem value="MALE">זכר</MenuItem>
              <MenuItem value="FEMALE">נקבה</MenuItem>
            </Select>
          </FormControl>
        </WizardField>

        <WizardField label="תאריך לידה">
          <DateField
            fullWidth
            size="small"
            value={values.dateOfBirth}
            format="DD/MM/YYYY"
            sx={fieldSx}
            onChange={value => onChange({ dateOfBirth: value })}
          />
        </WizardField>

        <WizardField label="תאריך סיום מנוי">
          <DateField
            fullWidth
            size="small"
            value={values.expirationDate}
            format="DD/MM/YYYY"
            sx={fieldSx}
            onChange={value => onChange({ expirationDate: value })}
          />
        </WizardField>

        <WizardField label="גובה (ס״מ)">
          <TextField
            fullWidth
            size="small"
            type="number"
            value={values.height}
            inputProps={{ min: 0, "aria-label": "גובה בסנטימטרים" }}
            sx={numberFieldSx}
            onChange={event => onChange({ height: event.target.value })}
          />
        </WizardField>

        <WizardField label="משקל (ק״ג)" hint="נשמר כשקילה ראשונה בגרף המשקל">
          <TextField
            fullWidth
            size="small"
            type="number"
            value={values.bodyWeight}
            inputProps={{ min: 0, step: 0.1, "aria-label": "משקל בקילוגרמים" }}
            sx={numberFieldSx}
            onChange={event => onChange({ bodyWeight: event.target.value })}
          />
        </WizardField>

        {trainerField}
      </StepGrid>

      <Box sx={{ marginTop: "26px", paddingTop: "20px", borderTop: `1px solid ${t.borderLight}` }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 700, color: t.text }}>סיסמה</Typography>
        <Typography sx={{ fontSize: "12px", color: t.t3, marginTop: "2px", marginBottom: "14px" }}>
          בין 8 ל-16 תווים. הלקוח יתבקש להחליף אותה בכניסה הראשונה.
        </Typography>

        <StepGrid>
          <WizardField label="סיסמה" required>
            <TextField
              fullWidth
              size="small"
              type={showPassword ? "text" : "password"}
              value={values.password}
              inputProps={{ "aria-label": "סיסמה" }}
              sx={fieldSx}
              onChange={event => onChange({ password: event.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      aria-label="הצג סיסמה"
                      onClick={() => setShowPassword(previous => !previous)}
                    >
                      {showPassword ? (
                        <VisibilityOff sx={{ fontSize: 18, color: t.t3 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: t.t3 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </WizardField>

          <WizardField
            label="אימות סיסמה"
            required
            error={passwordsMismatch ? "סיסמאות לא תואמות" : null}
          >
            <TextField
              fullWidth
              size="small"
              type={showConfirm ? "text" : "password"}
              value={values.passwordConfirm}
              error={passwordsMismatch}
              inputProps={{ "aria-label": "אימות סיסמה" }}
              sx={fieldSx}
              onChange={event => onChange({ passwordConfirm: event.target.value })}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      tabIndex={-1}
                      aria-label="הצג אימות סיסמה"
                      onClick={() => setShowConfirm(previous => !previous)}
                    >
                      {showConfirm ? (
                        <VisibilityOff sx={{ fontSize: 18, color: t.t3 }} />
                      ) : (
                        <Visibility sx={{ fontSize: 18, color: t.t3 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </WizardField>
        </StepGrid>

        <FormControlLabel
          sx={{ marginTop: "12px" }}
          control={
            <Checkbox
              size="small"
              checked={values.sendWelcomeEmail}
              onChange={event => onChange({ sendWelcomeEmail: event.target.checked })}
              sx={{ color: t.t3, "&.Mui-checked": { color: t.green } }}
            />
          }
          label={
            <Typography sx={{ fontSize: "13px", color: t.t2 }}>
              שליחת מייל הצטרפות ללקוח
            </Typography>
          }
        />
      </Box>
    </StepShell>
  );
};

export default IdentityStep;
